import React from "react";

/**
 * AltioraTreeLogo — Living tree logo (arbre arborescent vivant).
 *
 * A stylized organic branching tree in institutional navy with warm gold
 * leaves. The `alive` prop enables a subtle breathing pulse animation on the
 * leaves to convey life without distracting the eye.
 *
 * Palette: #0B2545 navy trunk/branches · #C5A880 gold leaves · #7A5B14 accents.
 * Meant to sit in ~40–80px containers (sidebar header, favicon, splash).
 */
export function AltioraTreeLogo({ size = 56, alive = true, className = "" }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 64 64"
      width={size}
      height={size}
      className={className}
      role="img"
      aria-label="ALTIORA COOP — arbre vivant"
      data-testid="altiora-tree-logo"
    >
      {/* Subtle radial glow behind the crown */}
      <defs>
        <radialGradient id="crown-glow" cx="50%" cy="35%" r="55%">
          <stop offset="0%" stopColor="#FBF6EA" stopOpacity="0.95" />
          <stop offset="70%" stopColor="#FBF6EA" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="trunk-grad" x1="0" y1="1" x2="0" y2="0">
          <stop offset="0%" stopColor="#0B2545" />
          <stop offset="100%" stopColor="#1F3A6B" />
        </linearGradient>
      </defs>

      <circle cx="32" cy="24" r="24" fill="url(#crown-glow)" />

      {/* --- Roots (grounding) --- */}
      <path
        d="M32 58 L26 61 M32 58 L38 61 M32 58 L22 60 M32 58 L42 60"
        stroke="#7A5B14"
        strokeWidth="1"
        strokeLinecap="round"
        opacity="0.7"
      />

      {/* --- Trunk --- */}
      <path
        d="M32 58 L32 34"
        stroke="url(#trunk-grad)"
        strokeWidth="3.5"
        strokeLinecap="round"
      />

      {/* --- Primary branches (organic asymmetric) --- */}
      <g stroke="#0B2545" strokeWidth="2.2" strokeLinecap="round" fill="none">
        {/* Left main branch */}
        <path d="M32 42 Q26 38 20 34" />
        {/* Right main branch */}
        <path d="M32 40 Q39 36 45 32" />
        {/* Upper left branch */}
        <path d="M32 34 Q27 28 22 24" />
        {/* Upper right branch */}
        <path d="M32 34 Q37 28 42 24" />
        {/* Central crown */}
        <path d="M32 34 L32 20" />
      </g>

      {/* --- Secondary twigs --- */}
      <g stroke="#0B2545" strokeWidth="1.2" strokeLinecap="round" fill="none" opacity="0.85">
        <path d="M20 34 L16 30" />
        <path d="M20 34 L18 38" />
        <path d="M45 32 L49 28" />
        <path d="M45 32 L47 36" />
        <path d="M22 24 L18 20" />
        <path d="M22 24 L23 19" />
        <path d="M42 24 L46 20" />
        <path d="M42 24 L41 19" />
        <path d="M32 20 L28 16" />
        <path d="M32 20 L36 16" />
      </g>

      {/* --- Leaves (gold, alive) --- */}
      <g fill="#C5A880" className={alive ? "altiora-tree-alive" : ""}>
        {/* Left cluster */}
        <ellipse cx="16" cy="30" rx="2.2" ry="1.6" transform="rotate(-30 16 30)" />
        <ellipse cx="18" cy="38" rx="2" ry="1.4" transform="rotate(20 18 38)" />
        <ellipse cx="18" cy="20" rx="2.2" ry="1.6" transform="rotate(-25 18 20)" />
        <ellipse cx="23" cy="19" rx="2" ry="1.4" transform="rotate(15 23 19)" />

        {/* Right cluster */}
        <ellipse cx="49" cy="28" rx="2.2" ry="1.6" transform="rotate(30 49 28)" />
        <ellipse cx="47" cy="36" rx="2" ry="1.4" transform="rotate(-20 47 36)" />
        <ellipse cx="46" cy="20" rx="2.2" ry="1.6" transform="rotate(25 46 20)" />
        <ellipse cx="41" cy="19" rx="2" ry="1.4" transform="rotate(-15 41 19)" />

        {/* Top cluster */}
        <ellipse cx="28" cy="16" rx="2" ry="1.4" transform="rotate(-15 28 16)" />
        <ellipse cx="36" cy="16" rx="2" ry="1.4" transform="rotate(15 36 16)" />
        <ellipse cx="32" cy="12" rx="2.4" ry="1.7" />
      </g>

      {/* --- Accent buds (deep gold) --- */}
      <g fill="#7A5B14" opacity="0.9">
        <circle cx="32" cy="20" r="1.3" />
        <circle cx="22" cy="24" r="0.9" />
        <circle cx="42" cy="24" r="0.9" />
        <circle cx="20" cy="34" r="0.8" />
        <circle cx="45" cy="32" r="0.8" />
      </g>
    </svg>
  );
}

export default AltioraTreeLogo;
